import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

const ANIMATION_STYLES = `
@keyframes animModalBackdropIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes animModalBackdropOut {
  from { opacity: 1; }
  to { opacity: 0; }
}
@keyframes animModalCardIn {
  from { opacity: 0; transform: scale(0.75); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes animModalCardOut {
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.75); }
}
`;

interface AnimatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  backdropStyle?: React.CSSProperties;
  cardStyle?: React.CSSProperties;
  zIndex?: number;
}

const AnimatedModal: React.FC<AnimatedModalProps> = ({
  isOpen,
  onClose,
  children,
  backdropStyle,
  cardStyle,
  zIndex = 9999
}) => {
  const [mounted, setMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setIsClosing(false);
    } else if (mounted) {
      setIsClosing(true);
    }
  }, [isOpen]);

  const handleAnimationEnd = () => {
    if (isClosing) {
      setMounted(false);
      setIsClosing(false);
    }
  };

  const handleBackdropClick = () => {
    setIsClosing(true);
    onClose();
  };

  if (!mounted) return null;

  return ReactDOM.createPortal(
    <>
      <style>{ANIMATION_STYLES}</style>
      <div
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex,
          background: 'rgba(0,0,0,0.72)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: isClosing
            ? 'animModalBackdropOut 250ms ease forwards'
            : 'animModalBackdropIn 250ms ease forwards',
          ...backdropStyle
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          onAnimationEnd={handleAnimationEnd}
          style={{
            animation: isClosing
              ? 'animModalCardOut 250ms cubic-bezier(0.4, 0, 0.2, 1) forwards'
              : 'animModalCardIn 350ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
            ...cardStyle
          }}
        >
          {children}
        </div>
      </div>
    </>,
    document.body
  );
};

export default AnimatedModal;
